import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getEngine, listRuns, startRun } from "../../api/client";
import {
  type CardStatus,
  type EngineInfo,
  type EngineOverrides,
  PRICE_PROMPT_TYPES,
  type PricePromptType,
  type Run,
} from "../../api/models";
import { StatusBadge } from "../../components/Badge";
import { EngineDetails } from "../../components/EngineDetails";
import { Progress } from "../../components/Progress";
import { usePoll } from "../../hooks/usePoll";
import { accuracy, pct } from "../../lib/accuracy";
import { errorMessage, formatDate } from "../../lib/format";

const active = (r: Run) => r.status === "RUNNING" || r.status === "QUEUED";

/** The form's fields, flat; a value equal to the default is not sent. */
interface EngineForm {
  identifyModel: string;
  pricePickerModel: string;
  identifyPrompt: string;
  price: Record<PricePromptType, string>;
}

const formOf = (e: EngineInfo): EngineForm => ({
  identifyModel: e.identify,
  pricePickerModel: e.pricePicker,
  identifyPrompt: e.prompts?.identify ?? "",
  price: Object.fromEntries(PRICE_PROMPT_TYPES.map((t) => [t, e.prompts?.price[t] ?? ""])) as Record<
    PricePromptType,
    string
  >,
});

export function overridesOf(form: EngineForm, defaults: EngineInfo): EngineOverrides | undefined {
  const base = formOf(defaults);
  const diff = <T extends string>(value: T, def: T) =>
    value.trim() !== def.trim() && value.trim() ? value : undefined;
  const models = {
    identify: diff(form.identifyModel, base.identifyModel),
    pricePicker: diff(form.pricePickerModel, base.pricePickerModel),
  };
  const price = Object.fromEntries(
    PRICE_PROMPT_TYPES.map((t) => [t, diff(form.price[t], base.price[t])]).filter(([, v]) => v !== undefined),
  ) as Partial<Record<PricePromptType, string>>;
  const prompts = {
    identify: diff(form.identifyPrompt, base.identifyPrompt),
    ...(Object.keys(price).length ? { price } : {}),
  };
  const out: EngineOverrides = {};
  if (models.identify || models.pricePicker) out.models = models;
  if (prompts.identify || prompts.price) out.prompts = prompts;
  return Object.keys(out).length ? out : undefined;
}

export function RunsPage() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<CardStatus[]>(["APPROVED", "NO_PRODUCT", "PENDING"]);
  const [limit, setLimit] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<EngineInfo | null>(null);
  const [customize, setCustomize] = useState(false);
  const [form, setForm] = useState<EngineForm | null>(null);

  const load = useCallback(() => {
    listRuns()
      .then(setRuns)
      .catch((e) => setError(errorMessage(e)));
  }, []);
  usePoll(load, 3000, runs === null || runs.some(active));

  useEffect(() => {
    getEngine()
      .then((e) => {
        setDefaults(e);
        setForm(formOf(e));
      })
      .catch((e) => setError(errorMessage(e)));
  }, []);

  const toggle = (s: CardStatus) =>
    setStatuses(statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStarting(true);
    setStartError(null);
    try {
      const engine = customize && form && defaults ? overridesOf(form, defaults) : undefined;
      const run = await startRun({ statuses, limit: limit ? Number(limit) : undefined, engine });
      navigate(`/runs/${run.id}`);
    } catch (err) {
      setStartError(errorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const overrides = customize && form && defaults ? overridesOf(form, defaults) : undefined;

  return (
    <>
      <div className="toolbar">
        <h1>Runs</h1>
        <span className="grow" />
        <form className="toolbar" style={{ marginBottom: 0 }} onSubmit={submit}>
          <label className="field">
            <input type="checkbox" checked={statuses.includes("APPROVED")} onChange={() => toggle("APPROVED")} />
            Approved
          </label>
          <label className="field">
            <input type="checkbox" checked={statuses.includes("NO_PRODUCT")} onChange={() => toggle("NO_PRODUCT")} />
            Not in catalog
          </label>
          <label className="field">
            <input type="checkbox" checked={statuses.includes("PENDING")} onChange={() => toggle("PENDING")} />
            Pending
          </label>
          <label className="field">
            limit
            <input type="number" min={1} max={1000} value={limit} onChange={(e) => setLimit(e.target.value)} />
          </label>
          <label className="field">
            <input type="checkbox" checked={customize} disabled={!form} onChange={() => setCustomize(!customize)} />
            Custom engine
          </label>
          <button type="submit" className="primary" disabled={starting || statuses.length === 0}>
            {overrides ? "Run custom engine" : "Run engine"}
          </button>
          {startError && <span className="error">{startError}</span>}
          {customize && form && defaults && (
            <div className="panel engine-form">
              <label>
                identify model
                <input
                  value={form.identifyModel}
                  onChange={(e) => setForm({ ...form, identifyModel: e.target.value })}
                />
              </label>
              <label>
                price picker model
                <input
                  value={form.pricePickerModel}
                  onChange={(e) => setForm({ ...form, pricePickerModel: e.target.value })}
                />
              </label>
              <label className="wide">
                identify prompt
                <textarea
                  value={form.identifyPrompt}
                  onChange={(e) => setForm({ ...form, identifyPrompt: e.target.value })}
                />
              </label>
              {PRICE_PROMPT_TYPES.map((t) => (
                <label key={t}>
                  price picker prompt · {t}
                  <textarea
                    value={form.price[t]}
                    onChange={(e) => setForm({ ...form, price: { ...form.price, [t]: e.target.value } })}
                  />
                </label>
              ))}
              <div className="wide muted">
                Only fields that differ from the deployed engine are sent.{" "}
                <button type="button" className="link" onClick={() => setForm(formOf(defaults))}>
                  Reset to defaults
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
      {error && <p className="error">{error}</p>}
      {!runs && !error && <p className="muted">Loading…</p>}
      {runs && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Run</th>
                <th>Created</th>
                <th>By</th>
                <th>Status</th>
                <th>Engine</th>
                <th className="num">Scored</th>
                <th className="num">Correct</th>
                <th className="num">Verified correct</th>
                <th className="num">Listings</th>
                <th className="num">Capped</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const s = r.summary;
                const acc = accuracy(s);
                return (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/runs/${r.id}`}>#{r.id}</Link>
                      {r.params.source === "report" && <span className="badge"> report</span>}
                      {r.params.label && <span className="muted"> {r.params.label}</span>}
                    </td>
                    <td className="nowrap">{formatDate(r.createdAt)}</td>
                    <td className="muted">
                      {r.createdBy}
                      {r.private && <span title="Only you see this run"> · private</span>}
                    </td>
                    <td className="nowrap">
                      <StatusBadge status={r.status} /> <Progress done={r.done} total={r.total} />
                      {r.error && <span className="error"> {r.error}</span>}
                    </td>
                    <td>
                      <EngineDetails engine={r.engine} defaults={defaults} />
                    </td>
                    <td className="num">{s ? s.scored : "—"}</td>
                    <td className="num">{s ? `${s.correct} (${acc.correct})` : "—"}</td>
                    <td className="num">{s ? `${s.verifiedCorrect}/${s.verified} (${acc.verified})` : "—"}</td>
                    <td className="num">{s ? `${s.ebayListings} (${pct(s.ebayListings, s.total)})` : "—"}</td>
                    <td className="num">{s ? s.cappedQueries : "—"}</td>
                  </tr>
                );
              })}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted">
                    No runs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
